if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onPopState = this.onPopState.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onScroll = this.onScroll.bind(this);
      }

      connectedCallback() {
        super.connectedCallback();
        if (this.viewerInitialized) return;
        this.viewerInitialized = true;

        this.content = this.querySelector('.product-media-modal__content');
        this.previousButton = this.querySelector('[data-product-media-previous]');
        this.nextButton = this.querySelector('[data-product-media-next]');
        this.counter = this.querySelector('[data-product-media-counter]');

        this.previousButton.addEventListener('click', () => this.movePhoto(-1));
        this.nextButton.addEventListener('click', () => this.movePhoto(1));
        this.content.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('keydown', this.onKeyDown);
        window.addEventListener('popstate', this.onPopState);
        window.addEventListener('resize', this.onResize);
      }

      show(opener) {
        super.show(opener);
        this.setActiveMedia(opener.getAttribute('data-media-id'), { behavior: 'auto' });
        this.updateHistory();
      }

      hide(skipHistory = false) {
        const shouldReturnToProduct = this.hasHistoryEntry && !skipHistory;
        super.hide();
        if (shouldReturnToProduct) {
          this.hasHistoryEntry = false;
          window.history.back();
        }
      }

      getPhotoItems() {
        return Array.from(this.querySelectorAll('.product-media-modal__content > img[data-media-id]')).filter(
          (image) => !image.classList.contains('product__media-item--variant')
        );
      }

      getActivePhoto() {
        return this.querySelector(
          '.product-media-modal__content > img.active[data-media-id]:not(.product__media-item--variant)'
        );
      }

      setActiveMedia(mediaId, { behavior = 'smooth', scroll = true, updateHistory = false } = {}) {
        const photos = this.getPhotoItems();
        const photo = photos.find((item) => item.dataset.mediaId === mediaId) || photos[0];
        if (!photo) return;

        this.querySelectorAll('.product-media-modal__content > img.active').forEach((item) => item.classList.remove('active'));
        photos.forEach((item) => item.classList.toggle('active', item === photo));
        this.classList.add('is-photo-gallery');
        this.updateViewerControls(photo);

        if (scroll) {
          window.requestAnimationFrame(() => this.scrollToPhoto(photo, behavior));
        }
        if (updateHistory) this.updateHistory();
      }

      scrollToPhoto(photo, behavior = 'smooth') {
        const left = photo.offsetLeft - (this.content.clientWidth - photo.clientWidth) / 2;
        this.content.scrollTo({ left, behavior });
      }

      updateViewerControls(activePhoto) {
        const photos = this.getPhotoItems();
        const index = photos.indexOf(activePhoto);
        const hasNavigation = index >= 0 && photos.length > 1;

        this.previousButton.hidden = !hasNavigation;
        this.nextButton.hidden = !hasNavigation;
        this.previousButton.disabled = !hasNavigation || index === 0;
        this.nextButton.disabled = !hasNavigation || index === photos.length - 1;
        this.counter.hidden = index < 0;
        this.counter.textContent = index >= 0 ? `${index + 1} of ${photos.length}` : '';
      }

      movePhoto(direction) {
        const photos = this.getPhotoItems();
        const nextPhoto = photos[photos.indexOf(this.getActivePhoto()) + direction];
        if (!nextPhoto) return;
        this.setActiveMedia(nextPhoto.dataset.mediaId, { updateHistory: true });
      }

      onScroll() {
        window.clearTimeout(this.scrollEndTimer);
        this.scrollEndTimer = window.setTimeout(() => this.syncActivePhotoFromScroll(), 80);
      }

      syncActivePhotoFromScroll() {
        const photos = this.getPhotoItems();
        if (!photos.length) return;
        const contentCenter = this.content.getBoundingClientRect().left + this.content.clientWidth / 2;
        const nearestPhoto = photos.reduce((nearest, photo) => {
          const photoCenter = photo.getBoundingClientRect().left + photo.clientWidth / 2;
          const nearestCenter = nearest.getBoundingClientRect().left + nearest.clientWidth / 2;
          return Math.abs(photoCenter - contentCenter) < Math.abs(nearestCenter - contentCenter) ? photo : nearest;
        });
        if (nearestPhoto !== this.getActivePhoto()) {
          this.setActiveMedia(nearestPhoto.dataset.mediaId, { scroll: false, updateHistory: true });
        }
      }

      onResize() {
        const activePhoto = this.getActivePhoto();
        if (activePhoto) this.scrollToPhoto(activePhoto, 'auto');
      }

      onKeyDown(event) {
        if (!this.hasAttribute('open') || event.target.closest('input, textarea, select')) return;
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.movePhoto(-1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.movePhoto(1);
        }
      }

      updateHistory() {
        const activePhoto = this.getActivePhoto();
        if (!activePhoto) return;
        const url = new URL(window.location.href);
        url.hash = `media-${activePhoto.dataset.mediaId}`;
        const state = { ...(window.history.state || {}), productMediaModal: this.id, mediaId: activePhoto.dataset.mediaId };
        if (this.hasHistoryEntry) {
          window.history.replaceState(state, '', url);
        } else {
          window.history.pushState(state, '', url);
          this.hasHistoryEntry = true;
        }
      }

      onPopState() {
        if (!this.hasAttribute('open')) return;
        this.hasHistoryEntry = false;
        this.hide(true);
      }
    }
  );
}
